import dayjs from "dayjs";

import { apiGet } from "@apis/gateway";
import { reportQueryStr } from "@utils/date";

const ANCHOR_ZONE = "Asia/Bangkok";

// One fetch for a report window that hides the backend's fixed-Bangkok reading
// of `dateTime` (see reportQueryStr in utils/date).
//
//   hours        the anchor is converted to Bangkok time and fetched once — the
//                window is an instant range, so the instant must line up.
//   days         the user's local day can straddle TWO Bangkok days (Taiwan's
//                day starts at Bangkok 23:00 the evening before; a European
//                one ends the next Bangkok morning). Every Bangkok day the
//                local day touches is fetched, the rows are trimmed back to
//                the local day, and the callers' minute-key join sees exactly
//                the day the user asked for. A Thai browser touches one day
//                and issues the same single request as before.
//   weeks/months one fetch on the user's date, unchanged: those charts are
//                per-day aggregates whose one-hour edge is invisible, and the
//                payloads are large. C9-T4 asks the backend to honour
//                X-Time-Zone, which retires all of this.
//
// `urlFor` receives the anchor string to put in the query so each caller keeps
// its own URL shape. Every response field except `data.data.result` is passed
// through from the first response.
export const apiGetReportWindow = async (
  urlFor: (anchor: string) => string,
  localAnchor: string,
  unit: string,
  language: string
): Promise<any> => {
  if (unit !== "days") {
    return apiGet(urlFor(reportQueryStr(localAnchor, unit)), {}, language);
  }
  const start = dayjs(localAnchor).startOf("day");
  const end = start.add(1, "day");
  // keep the caller's time-of-day so a single-day request is byte-identical
  // to what it always sent
  const timeOfDay = localAnchor.slice(11) || "00:00:00";
  const bkkDates = Array.from(
    new Set([
      start.tz(ANCHOR_ZONE).format("YYYY-MM-DD"),
      end.subtract(1, "minute").tz(ANCHOR_ZONE).format("YYYY-MM-DD"),
    ])
  );
  const responses: any[] = await Promise.all(
    bkkDates.map((d) => apiGet(urlFor(`${d} ${timeOfDay}`), {}, language))
  );
  const failed = responses.find((r) => !(r?.status && r?.code === 200));
  if (failed) return failed;
  if (responses.length === 1) return responses[0];

  // recordTime is UTC ISO; wifi-event rows carry a bare UTC `time` instead.
  // Rows with neither are kept — better a stray row than a silently dropped
  // one.
  const instant = (row: any): number | null => {
    if (row?.recordTime) return dayjs(row.recordTime).valueOf();
    if (row?.time)
      return new Date(`${String(row.time).replace(" ", "T")}Z`).valueOf();
    return null;
  };
  const rows: any[] = ([] as any[]).concat(
    ...responses.map((r) => r?.data?.data?.result ?? [])
  );
  const result = rows.filter((row) => {
    const t = instant(row);
    return t === null || (t >= start.valueOf() && t < end.valueOf());
  });
  return {
    ...responses[0],
    data: {
      ...responses[0].data,
      data: { ...(responses[0].data?.data ?? {}), result },
    },
  };
};
